<script>
	import { onMount } from "svelte";
	import { fade } from "svelte/transition";
	import { algorithmVersionAll as all } from "../../stores";
	import { postApi as post, putApi as put, delApi as del, postFileApi, putFileApi } from "../../service/api";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import CKEditor from "../../components/CKEditor.svelte";
	import { ErrorCodes } from "../../define/define";

	// CKEditor 인스턴스들 - 각 언어별로 별도 관리
	let editorInstances = {
		Ko: null,
		En: null,
		Ja: null,
		ZhCn: null,
		ZhTw: null,
	};

	// 상수 정의
	const BASE_URL = "/contents/notice";
	const PAGE_SIZES = [10, 15, 20, 25, 30, 50, 100];
	let pageSize = 10; // 페이지당 항목 수
	let totalItems = 0;

	const ACTIVE_OPTIONS = [
		{ value: true, text: "노출" },
		{ value: false, text: "미노출" },
	];

	// 상태 관리
	let isEditMode = false;
	let editingRowId = null;
	let filteredData = [];

	// 페이징 상태
	let currentPage = 1;
	let totalPages = 1;

	// 편집 아이템 초기값
	const defaultEditItem = {
		id: 0,
		subjectKo: "",
		contentKo: "",
		subjectEn: "",
		contentEn: "",
		subjectJa: "",
		contentJa: "",
		subjectZhCn: "",
		contentZhCn: "",
		subjectZhTw: "",
		contentZhTw: "",
		isActive: false,
		isHome: false,
		isLatest: false,
	};

	// 업로드 대기 파일 초기값
	const pendingFiles = {
		Ko: new Map(),
		En: new Map(),
		Ja: new Map(),
		ZhCn: new Map(),
		ZhTw: new Map(),
	};

	let editItem = { ...defaultEditItem };

	// 모달 상태 관리
	let showModal = false;
	let showViewModal = false;
	let modalTitle = "";
	let viewModalTitle = "";

	// 탭 관련 상태 - 하나로 통합
	let activeLanguageTab = "ko";
	let activeViewLanguageTab = "ko";

	// 저장 중 상태
	let isSaving = false;

	// 상세보기용 아이템
	let viewItem = { ...defaultEditItem };

	function extractImageUrls(html) {
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		return Array.from(doc.querySelectorAll("img")).map((img) => img.src);
	}

	const handleEditorReady = (event, lang) => {
		// 에디터 인스턴스 저장
		editorInstances[lang] = event.detail.editor;

		// 편집 모드일 때 기존 데이터가 있으면 설정
		if (editItem[`content${lang}`] && editItem[`content${lang}`] !== event.detail.data) {
			setTimeout(() => {
				if (editorInstances[lang]) {
					editorInstances[lang].setData(editItem[`content${lang}`]);
				}
			}, 100);
		}
	};

	const handleEditorChange = (event, lang) => {
		editItem[`content${lang}`] = event.detail.data;
		const currentImages = new Set(extractImageUrls(event.detail.data));

		// 삭제된 이미지 정리
		if (pendingFiles[lang]) {
			for (const [key, value] of pendingFiles[lang].entries()) {
				if (!currentImages.has(value.tempUrl)) {
					URL.revokeObjectURL(value.tempUrl); // 메모리 해제
					pendingFiles[lang].delete(key);
				}
			}
		}
	};

	// 각 언어별 업로드 대기파일의 파일명을 바꾸고 실제 경로를 조합하여 교체
	const makeFileArr = () => {
		const langs = ["Ko", "En", "Ja", "ZhCn", "ZhTw"];
		let files = [];

		// 배포 환경 별 S3 경로 생성
		let serverPath = localStorage.getItem("selectedServer").split("-")[0];
		switch (serverPath) {
			case "stg":
				serverPath = "https://stg-resource.sleepwave.ai";
				break;
			case "kr" || "us":
				serverPath = "https://resource.sleepwave.ai";
				break;
			default:
				serverPath = "https://dev-resource.sleepwave.ai";
		}
		const resourcePath = "/v3/resources/notice/";

		// 언어별 컨텐츠로부터 파일경로 교체
		for (const lang of langs) {
			for (const [key, value] of pendingFiles[lang].entries()) {
				// 파일명 교체
				const oFile = value.file;
				const ext = oFile.name.split(".").pop(); // 확장자
				const renamedFile = new File([oFile], `${key}.${ext}`, { type: oFile.type });
				console.log(renamedFile);
				console.log(`${serverPath}${resourcePath}${renamedFile.name}`);
				console.log(value.tempUrl);
				console.log(`content${lang}`);
				console.log(editItem[`content${lang}`].replace(value.tempUrl, `${serverPath}${resourcePath}${renamedFile.name}`));
				// 경로 교체
				editItem[`content${lang}`] = editItem[`content${lang}`].replace(value.tempUrl, `${serverPath}${resourcePath}${renamedFile.name}`);

				// 배열에 추가
				files.push(renamedFile);
			}
		}
		return files;
	};

	// 메서드
	const setFocus = (field = "subjectKo") => {
		setTimeout(() => {
			const element = document.getElementById(field);
			if (element) {
				element.focus();
			}
		}, 100);
	};

	const validateItem = () => {
		if (!editItem.subjectKo) {
			alert("한글 제목을 입력하세요.");
			setFocus("subjectKo");
			return false;
		}
		if (!editItem.contentKo) {
			alert("한글 내용을 입력하세요.");
			setFocus("contentKo");
			return false;
		}
		if (!editItem.subjectEn) {
			alert("영문 제목을 입력하세요.");
			setFocus("subjectEn");
			return false;
		}
		if (!editItem.contentEn) {
			alert("영문 내용을 입력하세요.");
			setFocus("contentEn");
			return false;
		}
		if (!editItem.subjectJa) {
			alert("일본어 제목을 입력하세요.");
			setFocus("subjectJa");
			return false;
		}
		if (!editItem.contentJa) {
			alert("일본어 내용을 입력하세요.");
			setFocus("contentJa");
			return false;
		}
		if (!editItem.subjectZhCn) {
			alert("중국어 간체 제목을 입력하세요.");
			setFocus("subjectZhCn");
			return false;
		}
		if (!editItem.contentZhCn) {
			alert("중국어 간체 내용을 입력하세요.");
			setFocus("contentZhCn");
			return false;
		}
		if (!editItem.subjectZhTw) {
			alert("중국어 번체 제목을 입력하세요.");
			setFocus("subjectZhTw");
			return false;
		}
		if (!editItem.contentZhTw) {
			alert("중국어 번체 내용을 입력하세요.");
			setFocus("contentZhTw");
			return false;
		}

		return true;
	};

	const handleSave = async () => {
		if (!validateItem()) return;

		isSaving = true;
		const files = makeFileArr();
		try {
			const payload = {
				...editItem,
				isActive: Boolean(editItem.isActive),
				isHome: Boolean(editItem.isHome),
				files: files,
			};

			const formData = new FormData();
			formData.append("subjectKo", editItem.subjectKo || "");
			formData.append("subjectEn", editItem.subjectEn || "");
			formData.append("subjectJa", editItem.subjectJa || "");
			formData.append("subjectZhCn", editItem.subjectZhCn || "");
			formData.append("subjectZhTw", editItem.subjectZhTw || "");
			formData.append("contentKo", editItem.contentKo || "");
			formData.append("contentEn", editItem.contentEn || "");
			formData.append("contentJa", editItem.contentJa || "");
			formData.append("contentZhCn", editItem.contentZhCn || "");
			formData.append("contentZhTw", editItem.contentZhTw || "");
			formData.append("isActive", editItem.isActive || "");
			formData.append("isHome", editItem.isHome || "");
			formData.append("isLatest", editItem.isLatest || "");
			files.forEach((file) => formData.append("files", file));

			if (editItem.id) {
				formData.append("id", editItem.id);
				const result = await putFileApi(BASE_URL, formData);
				if (result.resultCode === ErrorCodes.OK) {
					alert("공지사항이 수정되었습니다.");
					await fnSearch();
					closeModal();
				} else {
					alert(result.resultDescKo);
				}
			} else {
				const result = await postFileApi(BASE_URL, formData);
				if (result.resultCode === ErrorCodes.OK) {
					alert("공지사항이 등록되었습니다.");
					await fnSearch();
					closeModal();
				} else {
					alert(result.resultDescKo);
				}
			}
		} finally {
			isSaving = false;
		}
	};

	const fnSearch = async () => {
		await all.fetch(`${BASE_URL}/${pageSize}/${currentPage}`);
		resetEdit();
	};

	const resetEdit = () => {
		isEditMode = false;
		editingRowId = null;
		editItem = { ...defaultEditItem };
		activeLanguageTab = "ko";

		// pendingFiles 초기화
		Object.keys(pendingFiles).forEach((lang) => {
			pendingFiles[lang].clear();
		});

		// 에디터 인스턴스 정리
		Object.keys(editorInstances).forEach((lang) => {
			editorInstances[lang] = null;
		});
	};

	const fnProcessRegist = () => {
		editItem = { ...defaultEditItem };
		// pendingFiles 초기화
		Object.keys(pendingFiles).forEach((lang) => {
			pendingFiles[lang].clear();
		});
		modalTitle = '<i class="bx bx-plus-circle me-1"></i>공지사항 신규 등록';
		showModal = true;
		setFocus("subjectKo");
	};

	const fnProcessEdit = (item) => {
		editItem = { ...item };
		// pendingFiles 초기화
		Object.keys(pendingFiles).forEach((lang) => {
			pendingFiles[lang].clear();
		});
		modalTitle = '<i class="bx bx-edit me-1"></i>공지사항 정보 편집';
		showModal = true;
		console.log(editItem);
		setFocus("subjectKo");
	};

	const fnProcessView = (item) => {
		resetViewModal(); // 이전 데이터 정리
		viewItem = { ...item };
		viewModalTitle = '<i class="bx bx-eye me-1"></i>공지사항 상세보기';
		showViewModal = true;
		activeViewLanguageTab = "ko";
	};

	const fnSwitchToEdit = () => {
		// 상세보기에서 편집으로 전환
		const itemToEdit = { ...viewItem };
		closeViewModal();
		// 다음 틱에서 편집 모드 실행 (DOM 업데이트 후)
		setTimeout(() => {
			fnProcessEdit(itemToEdit);
		}, 10);
	};

	// 토글 상태 변경 처리 함수
	const handleToggleChange = async (id, field, newValue) => {
		try {
			const updateData = {
				id: id,
				[field]: newValue,
			};

			const response = await patch(`${BASE_URL}/${id}`, updateData);

			if (response.resultCode === ErrorCodes.OK) {
				// 성공 시 목록에서 해당 아이템 업데이트
				$all.data.list = $all.data.list.map((item) => (item.id === id ? { ...item, [field]: newValue } : item));

				// 토스트 메시지 표시
				const fieldName = field === "isActive" ? "노출여부" : field === "isHome" ? "홈 노출여부" : "최신여부";
				const statusText = newValue ? "노출" : "미노출";
				alert(`${fieldName}가 ${statusText}로 변경되었습니다.`);
			} else {
				throw new Error(response.resultDescKo || "업데이트에 실패했습니다.");
			}
		} catch (error) {
			console.error("토글 업데이트 실패:", error);
			alert("업데이트에 실패했습니다.");
			// 실패 시 원래 상태로 되돌림 (화면 새로고침)
			await fnSearch();
		}
	};

	const fnDelete = async (id) => {
		const item = $all.data.list.find((item) => item.id === id);
		if (item.isActive) {
			alert("노출 중인 공지사항은 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			const result = await del(BASE_URL, { id });
			if (result.resultCode === ErrorCodes.OK) {
				alert("공지사항이 삭제되었습니다.");
				await fnSearch();
			} else {
				alert(result.resultDescKo);
			}
		}
	};

	const fnRegistServer = () => {
		if (confirm("서버에 적용하시겠습니까?")) {
			put(`${BASE_URL}/cache`, {}).then((success) => {
				if (success) {
					alert("서버 적용이 완료되었습니다.");
				}
			});
		}
	};

	const closeModal = () => {
		showModal = false;
		resetEdit();
	};

	const closeViewModal = () => {
		showViewModal = false;
		activeViewLanguageTab = "ko";
		// 모달이 완전히 닫힌 후 데이터 정리
		setTimeout(() => {
			if (!showModal) {
				// 편집 모달이 열리지 않은 경우만 정리
				resetViewModal();
			}
		}, 300);
	};

	const resetViewModal = () => {
		viewItem = { ...defaultEditItem };
	};

	// 페이지 변경 핸들러
	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnSearch();
	};

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value); // pageSize 값 업데이트
		currentPage = 1; // 페이지를 1로 리셋
		fnSearch(); // 목록 새로고침
	};

	// 언어 탭 변경 - 하나로 통합
	const changeLanguageTab = (tab) => {
		activeLanguageTab = tab;
	};

	const changeViewLanguageTab = (tab) => {
		activeViewLanguageTab = tab;
	};

	$: {
		const data = $all?.data || {};
		filteredData = data.list || [];
		totalPages = Math.ceil((data.total || 0) / pageSize);
		totalItems = data.total || 0;
	}

	onMount(() => {
		menu.set("컨텐츠 관리");
		menuSub.set("공지사항 관리");
		menuSubSub.set("");
		fnSearch();
	});
</script>

<div class="card mb-4">
	<div class="table-responsive text-nowrap">
		<TableToolbar
			{totalItems}
			{currentPage}
			{totalPages}
			{pageSize}
			pageSizes={PAGE_SIZES}
			onPageSizeChange={handlePageSizeChange}
			onReload={fnSearch}
			onRegist={fnProcessRegist}
			onRegistServer={fnRegistServer}
		/>
		<div class="fade-container">
			<div class="table-container">
				<table class="custom-table">
					<thead>
						<tr style="text-align:center">
							<th width="80">No</th>
							<th width="*">제목</th>
							<th width="90">노출여부</th>
							<th width="90">홈 노출여부</th>
							<th width="90">최신여부</th>
							<th width="120">등록일시</th>
							<th width="120">수정일시</th>
							<th width="120">관리</th>
						</tr>
					</thead>
					<tbody>
						{#if filteredData.length === 0}
							<tr>
								<td colspan="7" class="text-center">등록된 데이터가 없습니다.</td>
							</tr>
						{:else}
							{#each filteredData as item, index}
								<tr style="text-align:center; font-size: 12px;">
									<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
									<td style="text-align:left;">
										<span class="badge bg-primary" style="font-size: 10px; font-weight: normal;">한국어</span>
										<span
											style="font-size: 12px; font-weight: bold; color: #0d6efd; cursor: pointer; text-decoration: underline;"
											on:click={() => fnProcessView(item)}
											on:keydown={(e) => e.key === "Enter" && fnProcessView(item)}
											role="button"
											tabindex="0"
										>
											{item.subjectKo}
										</span>
									</td>
									<td>
										<div class="form-check form-switch d-flex justify-content-center">
											<input class="form-check-input" type="checkbox" checked={item.isActive} disabled readonly />
										</div>
									</td>
									<td>
										<div class="form-check form-switch d-flex justify-content-center">
											<input class="form-check-input" type="checkbox" checked={item.isHome} disabled readonly />
										</div>
									</td>
									<td>
										<div class="form-check form-switch d-flex justify-content-center">
											<input class="form-check-input" type="checkbox" checked={item.isLatest} disabled readonly />
										</div>
									</td>
									<td>{Dates.defaultConvertFull(item.createdAt)}</td>
									<td>{Dates.defaultConvertFull(item.updatedAt)}</td>
									<td>
										<button class="btn btn-sm btn-outline-info me-1" on:click={() => fnProcessView(item)}>상세</button>
										<button class="btn btn-sm btn-outline-primary me-1" on:click={() => fnProcessEdit(item)}>편집</button>
										<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.id)}>삭제</button>
									</td>
								</tr>
								<!-- 다국어 제목 행들 (주석처리) -->
								<!-- 
								<tr>
									<td style="text-align:left;">
										<span class="badge bg-secondary" style="font-size: 10px; font-weight: normal;">영어</span>
										<span style="font-size: 12px;">{item.subjectEn}</span>
									</td>
								</tr>
								<tr>
									<td style="text-align:left;">
										<span class="badge bg-secondary" style="font-size: 10px; font-weight: normal;">일본어</span>
										<span style="font-size: 12px;">{item.subjectJa}</span>
									</td>
								</tr>
								<tr>
									<td style="text-align:left;">
										<span class="badge bg-secondary" style="font-size: 10px; font-weight: normal;">중국어 간체</span>
										<span style="font-size: 12px;">{item.subjectZhCn}</span>
									</td>
								</tr>
								<tr>
									<td style="text-align:left;">
										<span class="badge bg-secondary" style="font-size: 10px; font-weight: normal;">중국어 번체</span>
										<span style="font-size: 12px;">{item.subjectZhTw}</span>
									</td>
								</tr>
								-->
							{/each}
						{/if}
					</tbody>
				</table>
			</div>
			<style>
				.table-container {
					padding: 0 15px;
					width: 100%;
				}

				.custom-table {
					border-collapse: collapse;
					border: 1px solid #ddd;
					margin-bottom: 15px;
					font-family: var(--bs-body-font-family);
					font-size: 13px;
					box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
					width: 100%;
				}

				/* 헤더 스타일 */
				.custom-table thead tr {
					text-align: center;
					background-color: #f2f2f2;
					border-bottom: 2px solid #ddd;
				}

				/* 셀 공통 스타일 */
				.custom-table th,
				.custom-table td {
					padding: 4px;
					border: 1px solid #ddd;
					text-align: center;
				}

				/* 짝수/홀수 행 스타일 */
				.even-model {
					background-color: #ffffff;
					padding: 4px;
					border: 1px solid #ddd;
					font-size: 13px;
				}

				.odd-model {
					background-color: #f9f9f9;
					padding: 4px;
					border: 1px solid #ddd;
					font-size: 13px;
				}

				/* 호버 효과 */
				.custom-table tr:hover {
					background-color: #f0f8ff !important;
				}

				/* 데이터 없음 메시지 */
				.no-data {
					text-align: center;
				}

				/* 배지 스타일 */
				.badge {
					font-size: 13px;
					padding: 5px 10px;
				}

				/* 전역 스타일 - 필요한 경우 */
				:global(table) {
					font-family: var(--bs-body-font-family) !important;
					font-size: 12px !important;
				}

				:global(table td),
				:global(table th) {
					font-size: 12px !important;
				}

				.model-link {
					cursor: pointer;
					color: #0d6efd;
					text-decoration: underline;
				}
				.model-link:hover {
					color: #084298;
				}

				button {
					padding: 8px 16px;
					background-color: #4caf50;
					color: white;
					border: none;
					border-radius: 4px;
					cursor: pointer;
				}

				button:hover {
					background-color: #45a049;
				}

				.fade-container {
					opacity: 1;
					transition: opacity 0.15s linear;
				}

				.nav-tabs .nav-link {
					padding: 0.25rem 0.5rem;
					font-size: 0.75rem;
				}

				.nav-tabs-sm {
					border-bottom: 1px solid #b3acf2;
				}

				.nav-tabs-sm .nav-link {
					border-bottom: 1px solid #b3acf2;
					margin-right: 0.25rem;
					border-top-left-radius: 0.2rem;
					border-top-right-radius: 0.2rem;
				}

				.nav-tabs-sm .nav-link.active {
					border-top: 1px solid #b3acf2;
					border-left: 1px solid #b3acf2;
					border-right: 1px solid #b3acf2;
					font-weight: bold;
				}
			</style>
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
</div>

<!-- 모달 팝업 추가 -->
{#if showModal}
	<div
		class="modal fade {showModal ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered modal-xl">
			<div class="modal-content">
				<div class="modal-header bg-dark text-white">
					<h5 class="modal-title d-flex align-items-center" style="color: white !important;">{@html modalTitle}</h5>
					<button type="button" class="btn-close btn-close-white" on:click={closeModal}></button>
				</div>
				<div class="modal-body" style="height: calc(90vh - 120px);">
					<div class="row h-100">
						<!-- 좌측: 언어별 제목/내용 입력 -->
						<div class="col-md-7">
							<div class="mb-1">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-globe me-2"></i>
									언어별 내용 입력
								</h6>
								<ul class="nav nav-tabs nav-tabs-sm mb-3" role="tablist">
									<li class="nav-item">
										<a
											class="nav-link {activeLanguageTab === 'ko' ? 'active' : ''}"
											on:click|preventDefault={() => changeLanguageTab("ko")}
											href="#lang-ko"
											role="tab"
										>
											한국어
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeLanguageTab === 'en' ? 'active' : ''}"
											on:click|preventDefault={() => changeLanguageTab("en")}
											href="#lang-en"
											role="tab"
										>
											영어
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeLanguageTab === 'ja' ? 'active' : ''}"
											on:click|preventDefault={() => changeLanguageTab("ja")}
											href="#lang-ja"
											role="tab"
										>
											일본어
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeLanguageTab === 'zhCn' ? 'active' : ''}"
											on:click|preventDefault={() => changeLanguageTab("zhCn")}
											href="#lang-zhCn"
											role="tab"
										>
											간체
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeLanguageTab === 'zhTw' ? 'active' : ''}"
											on:click|preventDefault={() => changeLanguageTab("zhTw")}
											href="#lang-zhTw"
											role="tab"
										>
											번체
										</a>
									</li>
								</ul>

								<!-- 통합된 탭 컨텐츠 -->
								<div class="tab-content editor-tab-content" style="padding: 0px; max-height: calc(90vh - 280px); overflow-y: auto;">
									<!-- 한국어 탭 -->
									<div class="tab-pane fade {activeLanguageTab === 'ko' ? 'show active' : ''}" id="lang-ko" role="tabpanel">
										<div class="mb-2">
											<label for="subjectKo" class="form-label">제목 (한국어)</label>
											<input
												type="text"
												class="form-control form-control-sm"
												id="subjectKo"
												bind:value={editItem.subjectKo}
												placeholder="한글 제목을 입력하세요"
											/>
										</div>
										<div class="mb-2">
											<label for="contentKo" class="form-label">본문 (한국어)</label>
											<CKEditor
												bind:value={editItem.contentKo}
												bind:pendingFiles={pendingFiles.Ko}
												bind:this={editorInstances.Ko}
												config={{
													placeholder: "내용을 입력하세요...",
													maxHeight: "calc(90vh - 350px)",
												}}
												on:ready={(event) => handleEditorReady(event, "Ko")}
												on:change={(event) => handleEditorChange(event, "Ko")}
											/>
										</div>
									</div>

									<!-- 영어 탭 -->
									<div class="tab-pane fade {activeLanguageTab === 'en' ? 'show active' : ''}" id="lang-en" role="tabpanel">
										<div class="mb-2">
											<label for="subjectEn" class="form-label">제목 (영어)</label>
											<input
												type="text"
												class="form-control form-control-sm"
												id="subjectEn"
												bind:value={editItem.subjectEn}
												placeholder="영문 제목을 입력하세요"
											/>
										</div>
										<div class="mb-2">
											<label for="contentEn" class="form-label">본문 (영어)</label>
											<CKEditor
												bind:value={editItem.contentEn}
												bind:pendingFiles={pendingFiles.En}
												bind:this={editorInstances.En}
												config={{
													placeholder: "내용을 입력하세요...",
													maxHeight: "calc(90vh - 350px)",
												}}
												on:ready={(event) => handleEditorReady(event, "En")}
												on:change={(event) => handleEditorChange(event, "En")}
											/>
										</div>
									</div>

									<!-- 일본어 탭 -->
									<div class="tab-pane fade {activeLanguageTab === 'ja' ? 'show active' : ''}" id="lang-ja" role="tabpanel">
										<div class="mb-2">
											<label for="subjectJa" class="form-label">제목 (일본어)</label>
											<input
												type="text"
												class="form-control form-control-sm"
												id="subjectJa"
												bind:value={editItem.subjectJa}
												placeholder="일본어 제목을 입력하세요"
											/>
										</div>
										<div class="mb-2">
											<label for="contentJa" class="form-label">본문 (일본어)</label>
											<CKEditor
												bind:value={editItem.contentJa}
												bind:pendingFiles={pendingFiles.Ja}
												bind:this={editorInstances.Ja}
												config={{
													placeholder: "내용을 입력하세요...",
													maxHeight: "calc(90vh - 350px)",
												}}
												on:ready={(event) => handleEditorReady(event, "Ja")}
												on:change={(event) => handleEditorChange(event, "Ja")}
											/>
										</div>
									</div>

									<!-- 중국어 간체 탭 -->
									<div class="tab-pane fade {activeLanguageTab === 'zhCn' ? 'show active' : ''}" id="lang-zhCn" role="tabpanel">
										<div class="mb-2">
											<label for="subjectZhCn" class="form-label">제목 (중국어 간체)</label>
											<input
												type="text"
												class="form-control form-control-sm"
												id="subjectZhCn"
												bind:value={editItem.subjectZhCn}
												placeholder="중국어 간체 제목을 입력하세요"
											/>
										</div>
										<div class="mb-2">
											<label for="contentZhCn" class="form-label">본문 (중국어 간체)</label>
											<CKEditor
												bind:value={editItem.contentZhCn}
												bind:pendingFiles={pendingFiles.ZhCn}
												bind:this={editorInstances.ZhCn}
												config={{
													placeholder: "내용을 입력하세요...",
													maxHeight: "calc(90vh - 350px)",
												}}
												on:ready={(event) => handleEditorReady(event, "ZhCn")}
												on:change={(event) => handleEditorChange(event, "ZhCn")}
											/>
										</div>
									</div>

									<!-- 중국어 번체 탭 -->
									<div class="tab-pane fade {activeLanguageTab === 'zhTw' ? 'show active' : ''}" id="lang-zhTw" role="tabpanel">
										<div class="mb-2">
											<label for="subjectZhTw" class="form-label">제목 (중국어 번체)</label>
											<input
												type="text"
												class="form-control form-control-sm"
												id="subjectZhTw"
												bind:value={editItem.subjectZhTw}
												placeholder="중국어 번체 제목을 입력하세요"
											/>
										</div>
										<div class="mb-2">
											<label for="contentZhTw" class="form-label">본문 (중국어 번체)</label>
											<CKEditor
												bind:value={editItem.contentZhTw}
												bind:pendingFiles={pendingFiles.ZhTw}
												bind:this={editorInstances.ZhTw}
												config={{
													placeholder: "내용을 입력하세요...",
													maxHeight: "calc(90vh - 350px)",
												}}
												on:ready={(event) => handleEditorReady(event, "ZhTw")}
												on:change={(event) => handleEditorChange(event, "ZhTw")}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- 우측: 설정 및 시스템 정보 -->
						<div class="col-md-5 d-flex flex-column h-100">
							<!-- 설정 정보 -->
							<div class="mb-4 flex-shrink-0">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-cog me-2"></i>
									설정 정보
								</h6>
								<div class="row mb-3">
									<div class="col-4 fw-bold">노출여부</div>
									<div class="col-8">
										<div class="form-check form-switch">
											<input class="form-check-input" type="checkbox" id="isActive" bind:checked={editItem.isActive} />
											<label class="form-check-label" for="isActive">
												{editItem.isActive ? "노출" : "미노출"}
											</label>
										</div>
									</div>
								</div>
								<div class="row mb-3">
									<div class="col-4 fw-bold">홈 노출여부</div>
									<div class="col-8">
										<div class="form-check form-switch">
											<input class="form-check-input" type="checkbox" id="isHome" bind:checked={editItem.isHome} />
											<label class="form-check-label" for="isHome">
												{editItem.isHome ? "노출" : "미노출"}
											</label>
										</div>
									</div>
								</div>
								<div class="row mb-3">
									<div class="col-4 fw-bold">최신여부</div>
									<div class="col-8">
										<div class="form-check form-switch">
											<input class="form-check-input" type="checkbox" id="isLatest" bind:checked={editItem.isLatest} />
											<label class="form-check-label" for="isLatest">
												{editItem.isLatest ? "노출" : "미노출"}
											</label>
										</div>
									</div>
								</div>
							</div>

							{#if editItem.id}
								<!-- 시스템 정보 -->
								<div class="mb-4 flex-shrink-0">
									<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
										<i class="bx bx-info-circle me-2"></i>
										시스템 정보
									</h6>
									<div class="row mb-2">
										<div class="col-4 fw-bold">ID</div>
										<div class="col-8">{editItem.id}</div>
									</div>
									<div class="row mb-2">
										<div class="col-4 fw-bold">생성일시</div>
										<div class="col-8">{Dates.defaultConvertFull(editItem.createdAt)}</div>
									</div>
									<div class="row mb-2">
										<div class="col-4 fw-bold">수정일시</div>
										<div class="col-8">{Dates.defaultConvertFull(editItem.updatedAt)}</div>
									</div>
								</div>
							{/if}

							<!-- 미리보기 영역 -->
							<div class="flex-grow-1 d-flex flex-column" style="min-height: 0;">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded flex-shrink-0">
									<i class="bx bx-show me-2"></i>
									현재 활성 탭 미리보기
								</h6>
								<div class="border rounded p-3 bg-light flex-grow-1 d-flex flex-column" style="min-height: 0;">
									<div class="mb-2 flex-shrink-0">
										<strong>제목:</strong>
										{#if activeLanguageTab === "ko"}
											{editItem.subjectKo || "제목이 입력되지 않았습니다."}
										{:else if activeLanguageTab === "en"}
											{editItem.subjectEn || "제목이 입력되지 않았습니다."}
										{:else if activeLanguageTab === "ja"}
											{editItem.subjectJa || "제목이 입력되지 않았습니다."}
										{:else if activeLanguageTab === "zhCn"}
											{editItem.subjectZhCn || "제목이 입력되지 않았습니다."}
										{:else if activeLanguageTab === "zhTw"}
											{editItem.subjectZhTw || "제목이 입력되지 않았습니다."}
										{/if}
									</div>
									<div class="flex-grow-1 overflow-auto" style="min-height: 0;">
										<strong>내용:</strong>
										<div class="mt-2">
											{#if activeLanguageTab === "ko"}
												{@html editItem.contentKo || '<p class="text-muted">내용이 입력되지 않았습니다.</p>'}
											{:else if activeLanguageTab === "en"}
												{@html editItem.contentEn || '<p class="text-muted">내용이 입력되지 않았습니다.</p>'}
											{:else if activeLanguageTab === "ja"}
												{@html editItem.contentJa || '<p class="text-muted">내용이 입력되지 않았습니다.</p>'}
											{:else if activeLanguageTab === "zhCn"}
												{@html editItem.contentZhCn || '<p class="text-muted">내용이 입력되지 않았습니다.</p>'}
											{:else if activeLanguageTab === "zhTw"}
												{@html editItem.contentZhTw || '<p class="text-muted">내용이 입력되지 않았습니다.</p>'}
											{/if}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer d-flex justify-content-center">
					<button type="button" class="btn btn-sm btn-outline-primary" on:click={closeModal} disabled={isSaving}>취소</button>
					<button type="button" class="btn btn-sm btn-primary" on:click={handleSave} disabled={isSaving}>
						{#if isSaving}
							<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							처리 중...
						{:else if editItem.id}
							수정
						{:else}
							신규 등록
						{/if}
					</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade {showModal ? 'show' : ''}" transition:fade={{ duration: 200 }}></div>
{/if}

<!-- 상세보기 모달 팝업 -->
{#if showViewModal}
	<div
		class="modal fade {showViewModal ? 'show' : ''}"
		style="display: block;"
		tabindex="-1"
		aria-modal="true"
		role="dialog"
		transition:fade={{ duration: 200 }}
	>
		<div class="modal-dialog modal-dialog-centered modal-xl">
			<div class="modal-content">
				<div class="modal-header bg-dark text-white">
					<h5 class="modal-title d-flex align-items-center" style="color: white !important;">{@html viewModalTitle}</h5>
					<button type="button" class="btn-close btn-close-white" on:click={closeViewModal}></button>
				</div>
				<div class="modal-body">
					<div class="row">
						<!-- 좌측: 내용 미리보기 -->
						<div class="col-md-7">
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-show me-2"></i>
									언어별 내용 미리보기
								</h6>
								<ul class="nav nav-tabs nav-tabs-sm mb-3" role="tablist">
									<li class="nav-item">
										<a
											class="nav-link {activeViewLanguageTab === 'ko' ? 'active' : ''}"
											on:click|preventDefault={() => changeViewLanguageTab("ko")}
											href="#view-lang-ko"
											role="tab"
										>
											한국어
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeViewLanguageTab === 'en' ? 'active' : ''}"
											on:click|preventDefault={() => changeViewLanguageTab("en")}
											href="#view-lang-en"
											role="tab"
										>
											영어
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeViewLanguageTab === 'ja' ? 'active' : ''}"
											on:click|preventDefault={() => changeViewLanguageTab("ja")}
											href="#view-lang-ja"
											role="tab"
										>
											일본어
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeViewLanguageTab === 'zhCn' ? 'active' : ''}"
											on:click|preventDefault={() => changeViewLanguageTab("zhCn")}
											href="#view-lang-zhCn"
											role="tab"
										>
											간체
										</a>
									</li>
									<li class="nav-item">
										<a
											class="nav-link {activeViewLanguageTab === 'zhTw' ? 'active' : ''}"
											on:click|preventDefault={() => changeViewLanguageTab("zhTw")}
											href="#view-lang-zhTw"
											role="tab"
										>
											번체
										</a>
									</li>
								</ul>

								<div class="border rounded p-3" style="background-color: #f8f9fa;">
									<div class="mb-3">
										<h6 class="fw-bold">
											{#if activeViewLanguageTab === "ko"}
												제목: {viewItem.subjectKo || "제목이 없습니다."}
											{:else if activeViewLanguageTab === "en"}
												제목: {viewItem.subjectEn || "제목이 없습니다."}
											{:else if activeViewLanguageTab === "ja"}
												제목: {viewItem.subjectJa || "제목이 없습니다."}
											{:else if activeViewLanguageTab === "zhCn"}
												제목: {viewItem.subjectZhCn || "제목이 없습니다."}
											{:else if activeViewLanguageTab === "zhTw"}
												제목: {viewItem.subjectZhTw || "제목이 없습니다."}
											{/if}
										</h6>
									</div>
									<div style="max-height: 400px; overflow-y: auto;">
										<strong>내용:</strong>
										<div class="mt-2">
											{#if activeViewLanguageTab === "ko"}
												{@html viewItem.contentKo || '<p class="text-muted">내용이 없습니다.</p>'}
											{:else if activeViewLanguageTab === "en"}
												{@html viewItem.contentEn || '<p class="text-muted">내용이 없습니다.</p>'}
											{:else if activeViewLanguageTab === "ja"}
												{@html viewItem.contentJa || '<p class="text-muted">내용이 없습니다.</p>'}
											{:else if activeViewLanguageTab === "zhCn"}
												{@html viewItem.contentZhCn || '<p class="text-muted">내용이 없습니다.</p>'}
											{:else if activeViewLanguageTab === "zhTw"}
												{@html viewItem.contentZhTw || '<p class="text-muted">내용이 없습니다.</p>'}
											{/if}
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- 우측: 설정 및 시스템 정보 -->
						<div class="col-md-5">
							<!-- 기본 정보 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-info-circle me-2"></i>
									기본 정보
								</h6>
								<div class="row mb-2">
									<div class="col-4 fw-bold">ID</div>
									<div class="col-8">{viewItem.id}</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">노출여부</div>
									<div class="col-8">
										<span class="badge {viewItem.isActive ? 'bg-success' : 'bg-secondary'}">
											{viewItem.isActive ? "노출" : "미노출"}
										</span>
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">홈 노출여부</div>
									<div class="col-8">
										<span class="badge {viewItem.isHome ? 'bg-success' : 'bg-secondary'}">
											{viewItem.isHome ? "노출" : "미노출"}
										</span>
									</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">최신여부</div>
									<div class="col-8">
										<span class="badge {viewItem.isLatest ? 'bg-success' : 'bg-secondary'}">
											{viewItem.isLatest ? "노출" : "미노출"}
										</span>
									</div>
								</div>
							</div>

							<!-- 다국어 제목 요약 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-globe me-2"></i>
									다국어 제목 요약
								</h6>
								<div class="row mb-2">
									<div class="col-4 fw-bold">한국어</div>
									<div class="col-8 text-truncate" title={viewItem.subjectKo}>{viewItem.subjectKo || "-"}</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">영어</div>
									<div class="col-8 text-truncate" title={viewItem.subjectEn}>{viewItem.subjectEn || "-"}</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">일본어</div>
									<div class="col-8 text-truncate" title={viewItem.subjectJa}>{viewItem.subjectJa || "-"}</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">중국어 간체</div>
									<div class="col-8 text-truncate" title={viewItem.subjectZhCn}>{viewItem.subjectZhCn || "-"}</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">중국어 번체</div>
									<div class="col-8 text-truncate" title={viewItem.subjectZhTw}>{viewItem.subjectZhTw || "-"}</div>
								</div>
							</div>

							<!-- 시스템 정보 -->
							<div class="mb-4">
								<h6 class="border-bottom pb-2 mb-3 d-flex align-items-center bg-light p-2 rounded">
									<i class="bx bx-cog me-2"></i>
									시스템 정보
								</h6>
								<div class="row mb-2">
									<div class="col-4 fw-bold">생성일시</div>
									<div class="col-8">{Dates.defaultConvertFull(viewItem.createdAt)}</div>
								</div>
								<div class="row mb-2">
									<div class="col-4 fw-bold">수정일시</div>
									<div class="col-8">{Dates.defaultConvertFull(viewItem.updatedAt)}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="modal-footer d-flex justify-content-center">
					<button type="button" class="btn btn-sm btn-secondary" on:click={closeViewModal}>닫기</button>
					<button type="button" class="btn btn-sm btn-primary" on:click={fnSwitchToEdit}>편집하기</button>
				</div>
			</div>
		</div>
	</div>
	<div class="modal-backdrop fade {showViewModal ? 'show' : ''}" transition:fade={{ duration: 200 }}></div>
{/if}

<!-- 모달 높이 제한 스타일 -->
<style>
	/* 모달 최대 높이 제한 */
	:global(.modal-content) {
		max-height: 90vh;
		overflow: hidden;
	}

	:global(.modal-body) {
		max-height: calc(90vh - 120px);
		overflow-y: auto;
	}

	/* CKEditor 드롭다운 메뉴가 모달 위에 표시되도록 z-index 조정 */
	:global(.ck.ck-dropdown__panel) {
		z-index: 9999 !important;
	}

	:global(.ck.ck-balloon-panel) {
		z-index: 9999 !important;
	}

	:global(.ck.ck-toolbar__items) {
		z-index: 9999 !important;
	}

	:global(.ck.ck-dropdown__panel.ck-dropdown__panel_visible) {
		z-index: 9999 !important;
	}

	/* CKEditor 툴바 드롭다운 메뉴 스타일 */
	:global(.ck.ck-dropdown__panel.ck-dropdown__panel_s) {
		z-index: 9999 !important;
		position: fixed !important;
	}

	/* CKEditor 컨테이너의 overflow 설정 */
	:global(.ck-editor__main) {
		overflow: visible !important;
	}

	:global(.ck-editor) {
		overflow: visible !important;
	}

	/* 모달 내 CKEditor 툴바 줄바꿈 강화 스타일 */
	:global(.modal-body .ck-toolbar) {
		flex-wrap: wrap !important;
		max-width: 100% !important;
		overflow: visible !important;
	}

	:global(.modal-body .ck-toolbar__items) {
		flex-wrap: wrap !important;
		max-width: 100% !important;
		overflow: visible !important;
		white-space: normal !important;
	}

	/* 툴바 그룹들이 줄바꿈될 수 있도록 */
	:global(.modal-body .ck-toolbar__group) {
		flex-wrap: wrap !important;
		max-width: 100% !important;
	}

	/* 툴바 아이템들 사이 여백 조정 */
	:global(.modal-body .ck-toolbar__items > .ck-toolbar__group) {
		margin: 1px 2px !important;
	}

	/* 구분선도 줄바꿈에 포함 */
	:global(.modal-body .ck-toolbar__separator) {
		margin: 1px 2px !important;
	}

	/* 드롭다운 버튼들의 크기 조정 */
	:global(.modal-body .ck-dropdown__button) {
		white-space: nowrap !important;
		max-width: none !important;
	}

	/* CKEditor 컨테이너 스크롤 처리 */
	:global(.modal-body .ckeditor-container) {
		max-height: calc(90vh - 300px);
		overflow-y: auto;
		border: 1px solid #e0e0e0;
		border-radius: 4px;
	}

	/* 에디터 탭 컨텐츠 스크롤 */
	.editor-tab-content {
		scrollbar-width: thin;
		scrollbar-color: #c1c1c1 #f1f1f1;
	}

	.editor-tab-content::-webkit-scrollbar {
		width: 8px;
	}

	.editor-tab-content::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 4px;
	}

	.editor-tab-content::-webkit-scrollbar-thumb {
		background: #c1c1c1;
		border-radius: 4px;
	}

	.editor-tab-content::-webkit-scrollbar-thumb:hover {
		background: #a1a1a1;
	}

	/* CKEditor 자체 스크롤 스타일 개선 */
	:global(.modal-body .ck-editor__editable::-webkit-scrollbar) {
		width: 8px;
	}

	:global(.modal-body .ck-editor__editable::-webkit-scrollbar-track) {
		background: #f8f9fa;
		border-radius: 4px;
	}

	:global(.modal-body .ck-editor__editable::-webkit-scrollbar-thumb) {
		background: #dee2e6;
		border-radius: 4px;
	}

	:global(.modal-body .ck-editor__editable::-webkit-scrollbar-thumb:hover) {
		background: #adb5bd;
	}

	/* 모달 본문 스크롤 개선 */
	:global(.modal-body) {
		scrollbar-width: thin;
		scrollbar-color: #c1c1c1 #f1f1f1;
	}

	:global(.modal-body::-webkit-scrollbar) {
		width: 12px;
	}

	:global(.modal-body::-webkit-scrollbar-track) {
		background: #f1f1f1;
		border-radius: 6px;
	}

	:global(.modal-body::-webkit-scrollbar-thumb) {
		background: #c1c1c1;
		border-radius: 6px;
	}

	:global(.modal-body::-webkit-scrollbar-thumb:hover) {
		background: #a1a1a1;
	}
</style>
